import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { clearCachedUser, getStoredToken } from '@/lib/authCache';

const INACTIVITY_MS = 15 * 60 * 1000;  // 15 minutes
const WARNING_MS   = 14 * 60 * 1000;  // warn at 14 min (1 min before)

const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password', '/terms', '/contact', '/ranking'];

export function useInactivityLogout({ onWarn, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logoutTimer  = useRef(null);
  const warnTimer    = useRef(null);
  const warned       = useRef(false);

  const isPublic =
    PUBLIC_PATHS.includes(pathname) ||
    pathname.startsWith('/results/') ||
    pathname.startsWith('/api/');

  const doLogout = useCallback(async () => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warnTimer.current);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    clearCachedUser();
    onLogout?.();
    navigate('/login');
  }, [navigate, onLogout]);

  const resetTimers = useCallback(() => {
    if (isPublic) return;
    clearTimeout(logoutTimer.current);
    clearTimeout(warnTimer.current);
    warned.current = false;

    warnTimer.current = setTimeout(() => {
      warned.current = true;
      onWarn?.();
    }, WARNING_MS);

    logoutTimer.current = setTimeout(() => {
      doLogout();
    }, INACTIVITY_MS);
  }, [isPublic, doLogout, onWarn]);

  // Reset on user activity
  useEffect(() => {
    if (isPublic || !getStoredToken()) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handler = () => resetTimers();

    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(logoutTimer.current);
      clearTimeout(warnTimer.current);
    };
  }, [isPublic, resetTimers]);

  return { doLogout, resetTimers };
}
